/**
 * メインのゲームシーン。
 *
 * 責務は「Phaser とシステム群の接続」に限定し、
 * 判定ロジック自体は systems/ 側（Phaser 非依存）に置いている。
 */

import Phaser from 'phaser';

import { GameState } from '../../core/GameState';
import { GAME_TUNING } from '../config/gameConfig';
import type { ScenePhase, StageItemDefinition } from '../config/types';
import { ASSIST_TUNING } from '../../meta/assist';
import { bowlSkinById, type BowlSkin } from '../config/bowlSkins';
import { seededRandom } from '../../meta/daily';
import { computeAllRewardStatuses, findNewlyAvailableRewards } from '../config/rewardStatus';
import { resolveSpawnWeights } from '../config/validate';
import { MergeEffects } from '../effects/MergeEffects';
import { FoodPiece } from '../entities/FoodPiece';
import type { GameContext } from '../GameContext';
import { GameOverSystem, findSpilledPiece, findViolatingPiece } from '../systems/GameOverSystem';
import { MergeSystem, type MergeRequest } from '../systems/MergeSystem';
import { PendingDrop } from '../systems/PendingDrop';
import { PickSession } from '../systems/PickSession';
import { ScoreSystem } from '../systems/ScoreSystem';
import { SpawnSystem } from '../systems/SpawnSystem';
import { imageTextureKey, placeholderTextureKey } from '../utils/assetLoader';
import { resolveItemShape, type ResolvedShape } from '../utils/itemShape';
import { shapeScaleOf } from '../config/shapeScales';
import { stageIdOf } from '../config/stages';
import { createBowlGeometry, offsetContour } from '../utils/bowlGeometry';
import { STEAM_TEXTURE_KEY } from '../utils/sceneryTextures';
import { BACKGROUND_SCENE_KEY } from './BackgroundScene';

export const GAME_SCENE_KEY = 'GameScene';

export class GameScene extends Phaser.Scene {
  private context!: GameContext;
  private spawn!: SpawnSystem;
  private scoring!: ScoreSystem;
  private merge!: MergeSystem;
  private gameOver!: GameOverSystem;
  private effects!: MergeEffects;

  private readonly pieces = new Map<number, FoodPiece>();
  private readonly bodyIndex = new Map<number, FoodPiece>();
  private nextPieceId = 1;

  /** プレイエリア（器）の形状。長方形ではなく丼型で、口からあふれると終了。 */
  private readonly bowl = createBowlGeometry(GAME_TUNING.world.bowl);

  private gameTime = 0;
  private lastDropAt = Number.NEGATIVE_INFINITY;
  /** 待ち時間中のタップを1回分だけ取っておき、待ちが明けたらその位置に落とす。 */
  private readonly pendingDrop = new PendingDrop();
  private lastSafetyScanAt = 0;
  private highestLevelCreated = 0;
  /** このプレイで落とした数と、遊んだ時間（一時停止中は数えない）。ランキングの点数の妥当性確認に送る。 */
  private dropsThisRun = 0;
  private runStartedAt = 0;
  private scenePhase: ScenePhase = 'dusk';
  private dropX: number = GAME_TUNING.world.bowl.centerX;
  /** 背景ツアー累積時間のうち、まだ storage へ書き込んでいない分。 */
  private tourDeltaSincePersist = 0;

  private guideGraphics!: Phaser.GameObjects.Graphics;
  private lineGraphics!: Phaser.GameObjects.Graphics;
  /** 「こぼれそう！」の警告表示。 */
  private spillLabel?: Phaser.GameObjects.Text;
  private previewImage!: Phaser.GameObjects.Image;
  /** 料理をつまんで構える箸（落とす役）。 */
  private chopsticks!: Phaser.GameObjects.Graphics;
  /** 落とした直後に箸を少し開く演出の残り時間。 */
  private chopsticksOpenMs = 0;
  private dangerRatio = -1;
  /** 「こぼれそう！」が続いている状態か（board:danger を変わったときだけ出すため）。 */
  private dangerActive = false;
  /** おはしで消す料理を選んでいるあいだ true（物理は止めてある）。 */
  private pickMode = false;
  /** 選ぶモードの決まり：タップは選ぶだけ。「はい」で初めておはしを1本使う。 */
  private readonly pick = new PickSession(() => this.context.assist.consumeItem());
  /** 選んだ料理を光らせる輪。 */
  private pickRing?: Phaser.GameObjects.Graphics;
  /**
   * のんびりモード：器からこぼれてもゲームオーバーにならない（こぼれた料理は消える）。
   * スコアを競わずに料理と景色を楽しむためのもの。ハイスコア・メダル・今日の県には入れない。
   */
  private relaxed = false;
  /** こぼれた料理を見せている最中（1秒ほど）。この間は入力も物理も止める。 */
  private ending = false;
  private endingMarker?: Phaser.GameObjects.Graphics;
  /** 終わるきっかけになった、こぼれた料理。 */
  private spilledInfo?: { name: string; level: number; side: 'left' | 'right' };
  /** このプレイのスコアのうち、累計スコアへ加算済みの分（コンティニューで二重に足さないため）。 */
  private scoreCommittedToTotal = 0;

  private readonly unsubscribers: Array<() => void> = [];

  constructor() {
    super({ key: GAME_SCENE_KEY });
  }

  create(): void {
    this.context = this.registry.get('context') as GameContext;

    const daily = this.context.daily;
    this.spawn = new SpawnSystem({
      weights: resolveSpawnWeights(this.context.stage),
      maxLevel: this.context.maxLevel,
      // 今日の県は、やり直しても全員同じ順番で落ちてくる
      rngFactory: daily ? () => seededRandom(daily.seed) : undefined,
    });
    this.scoring = new ScoreSystem({
      mode: GAME_TUNING.score.mode,
      base: GAME_TUNING.score.base,
      comboWindowMs: GAME_TUNING.combo.windowMs,
      comboMultipliers: GAME_TUNING.combo.multipliers,
    });
    this.merge = new MergeSystem({
      maxLevel: this.context.maxLevel,
      bonusCooldownMs: GAME_TUNING.merge.maxLevelBonusCooldownMs,
      now: () => this.gameTime,
    });
    this.gameOver = new GameOverSystem({
      thresholdMs: GAME_TUNING.gameOver.warningMs,
      ignoreNewPieceMs: GAME_TUNING.gameOver.ignoreNewPieceMs,
      lineMarginPx: GAME_TUNING.gameOver.lineMarginPx,
      // 警告表示用なので、収まればまた計測をやり直す
      latch: false,
    });
    this.effects = new MergeEffects(this, this.context.settings);

    this.buildWorld();
    this.buildDecoration();
    this.setupInput();
    this.setupCollisions();
    this.bindCommands();

    this.matter.world.pause();
    this.emitQueueChange();
    this.context.bus.emit('game:ready', { stageTitle: this.context.stage.title });
    this.context.bus.emit('highscore:change', { highScore: this.context.storage.highScore });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  override update(_time: number, delta: number): void {
    const state = this.context.state;
    this.updatePreview();

    if (!state.simulates || this.pickMode || this.ending) return;

    this.gameTime += delta;
    this.flushPendingDrop();

    this.pieces.forEach((piece) => {
      piece.clampVelocity();
      piece.sync(delta);
    });
    this.processMerges();
    this.runSafetyScan();

    if (this.scoring.updateCombo(this.gameTime)) {
      this.context.bus.emit('combo:change', { combo: 0, multiplier: 1 });
    }

    this.accumulateTourElapsed(delta);
    this.updateTour();
    this.updateGameOver(delta);
  }

  /** PLAYING 中の経過時間を背景ツアーの累積時間に積み上げる。5秒に1回 storage へ保存。 */
  private accumulateTourElapsed(delta: number): void {
    this.tourDeltaSincePersist += delta;
    const PERSIST_INTERVAL_MS = 5000;
    if (this.tourDeltaSincePersist >= PERSIST_INTERVAL_MS) {
      this.context.storage.addTourElapsedMs(this.tourDeltaSincePersist);
      this.tourDeltaSincePersist = 0;
    }
  }

  /** 保存が滞っていれば即時書き込む（一時停止・ゲームオーバー・退避時）。 */
  private persistTourElapsed(): void {
    if (this.tourDeltaSincePersist > 0) {
      this.context.storage.addTourElapsedMs(this.tourDeltaSincePersist);
      this.tourDeltaSincePersist = 0;
    }
  }

  private updateTour(): void {
    const bg = this.scene.get(BACKGROUND_SCENE_KEY) as
      | import('./BackgroundScene').BackgroundScene
      | undefined;
    // tourDeltaSincePersist に今フレーム分まで積み上げ済みなので、
    // ここで gameTime を足すと同じ時間を二重に数えてツアーが倍速で進んでしまう。
    const totalElapsedMs = this.context.storage.tourElapsedMs + this.tourDeltaSincePersist;
    bg?.updateTour(totalElapsedMs);
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.persistTourElapsed();
    }
  };

  /* --------------------------------- 構築 --------------------------------- */

  /**
   * 器の内壁を線分（回転させた矩形）で近似して作る。
   * 器の外側には床も壁も置かない。こぼれた料理はそのまま落ちていき、
   * updateGameOver 側の「こぼれ判定」で終了になる。
   */
  private buildWorld(): void {
    const physics = GAME_TUNING.physics;
    const thickness = GAME_TUNING.world.bowl.wallThickness;
    const wallOptions = {
      isStatic: true,
      friction: physics.wallFriction,
      frictionStatic: physics.wallFriction,
      restitution: physics.wallRestitution,
    };

    const bowl = this.bowl;
    // 法線の向きを決める基準点（器の内側の中心付近）
    const focusY = bowl.rimY + bowl.depth * 0.5;
    const contour = bowl.contour;

    for (let i = 0; i < contour.length - 1; i += 1) {
      const a = contour[i];
      const b = contour[i + 1];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy);
      if (length < 0.5) continue;

      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      // 内側の面を輪郭に合わせるため、外向きの法線へ厚みの半分だけずらす
      let nx = -dy / length;
      let ny = dx / length;
      if ((midX - bowl.centerX) * nx + (midY - focusY) * ny < 0) {
        nx = -nx;
        ny = -ny;
      }

      this.matter.add.rectangle(
        midX + nx * (thickness / 2),
        midY + ny * (thickness / 2),
        length + 6, // 隣の線分と少し重ねて継ぎ目の隙間を防ぐ
        thickness,
        { ...wallOptions, angle: Math.atan2(dy, dx), label: `bowl-wall-${i}` },
      );
    }
  }

  private buildDecoration(): void {
    const { width, spawnY } = GAME_TUNING.world;
    const theme = this.context.stage.theme;

    this.drawBowl();

    // 上部の暖簾（屋台の入口をくぐったイメージ）。背景の梁を隠して額縁の役目も果たす
    const noren = this.add.graphics().setDepth(1);
    const norenColors = [theme.primaryColor, theme.accentColor];
    const stripeWidth = width / 8;
    for (let i = 0; i < 8; i += 1) {
      noren.fillStyle(Phaser.Display.Color.HexStringToColor(norenColors[i % 2]).color, 0.96);
      noren.fillRect(i * stripeWidth, 0, stripeWidth - 2, 26);
      // 裾の影で布らしく
      noren.fillStyle(0x000000, 0.18);
      noren.fillRect(i * stripeWidth, 20, stripeWidth - 2, 6);
    }
    noren.fillStyle(0x000000, 0.3);
    noren.fillRect(0, 25, width, 4);

    // 県名は暖簾の縞（primaryColor / accentColor の交互）に重なると
    // 明るい縞の上でコントラストが消えて読めなくなる。
    // 暖簾から下がった木札を1枚敷き、その上に置く。
    // スマホではキャンバスが 0.7 倍前後に縮むため、実寸で読める大きさにしておく
    // （16px だと 375px 幅の端末で 11px まで落ちる）
    const prefectureLabel = this.add
      .text(width / 2, 19, this.context.stage.regionName, {
        fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif',
        fontSize: '19px',
        color: '#f6e3c0',
      })
      .setOrigin(0.5)
      .setDepth(2);

    const plaqueWidth = Math.round(prefectureLabel.width + 38);
    const plaqueHeight = 34;
    const plaqueX = Math.round(width / 2 - plaqueWidth / 2);
    const plaqueY = 2;
    const plaque = this.add.graphics().setDepth(1.5);
    // 落ち影（暖簾から下がって見えるように）
    plaque.fillStyle(0x000000, 0.35);
    plaque.fillRoundedRect(plaqueX + 2, plaqueY + 3, plaqueWidth, plaqueHeight, 5);
    // 木札本体
    plaque.fillStyle(0x2a1512, 0.96);
    plaque.fillRoundedRect(plaqueX, plaqueY, plaqueWidth, plaqueHeight, 5);
    // 木の縁
    plaque.lineStyle(1.5, 0x8a5334, 0.9);
    plaque.strokeRoundedRect(plaqueX, plaqueY, plaqueWidth, plaqueHeight, 5);

    this.guideGraphics = this.add.graphics().setDepth(4);
    this.lineGraphics = this.add.graphics().setDepth(5);
    this.drawGameOverLine(0);

    this.spillLabel = this.add
      .text(this.bowl.centerX, this.bowl.rimY - 16, 'こぼれそう！', {
        // 細く薄い字が料理の後ろに隠れていた（2026-10-04）。太く濃くして縁取りし、料理より手前に出す
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '19px',
        fontStyle: '900',
        color: '#ffffff',
        stroke: '#a8121a',
        strokeThickness: 6,
      })
      .setOrigin(0.5, 1)
      // 料理は 10＋段数（最大22）。箸・プレビュー（8〜9）や得点の文字（70）とぶつからない高さ
      .setDepth(40)
      .setAlpha(0);

    const firstItem = this.context.items[0];
    this.previewImage = this.add
      .image(this.dropX, spawnY, this.textureKeyFor(firstItem))
      .setDepth(8)
      .setAlpha(0.95);
    this.applyShapeToImage(this.previewImage, this.shapeFor(firstItem));
    this.chopsticks = this.add.graphics().setDepth(9);
  }

  /**
   * 箸を描く（落とす役）。スイカゲームの「雲の子」にあたる、このゲームの顔。
   * 料理のてっぺんを右上から2本でつまむ。落とした直後は少し開く。
   */
  private drawChopsticks(x: number, topY: number, visible: boolean): void {
    const g = this.chopsticks;
    g.clear();
    if (!visible) return;
    const open = Phaser.Math.Clamp(this.chopsticksOpenMs / 160, 0, 1);
    const len = 118;
    const spread = 5 + open * 9;
    // 2本とも右上から差し込む。先端は料理の頭の両脇
    const tips = [
      { x: x - 7 - spread, y: topY + 6 },
      { x: x + 7 + spread, y: topY + 4 },
    ];
    // 先はつまみ、手元は開く（2本の角度を少し変える）
    const angles = [Phaser.Math.DegToRad(66 + open * 4), Phaser.Math.DegToRad(52 - open * 4)];
    tips.forEach((tip, i) => {
      const tailX = tip.x + Math.cos(angles[i]) * len;
      const tailY = tip.y - Math.sin(angles[i]) * len;
      // 影
      g.lineStyle(6, 0x1a0c08, 0.28);
      g.lineBetween(tip.x + 2, tip.y + 2, tailX + 2, tailY + 2);
      // 本体（塗り箸：先は細く白木、持ち手は朱）
      g.lineStyle(5, 0x8d2027, 1);
      g.lineBetween((tip.x + tailX * 2) / 3, (tip.y + tailY * 2) / 3, tailX, tailY);
      g.lineStyle(3.5, 0xe9cfa3, 1);
      g.lineBetween(tip.x, tip.y, (tip.x + tailX * 2) / 3, (tip.y + tailY * 2) / 3);
      g.lineStyle(1, 0xffffff, 0.35);
      g.lineBetween(tip.x + 1, tip.y - 1, tailX + 1, tailY - 1);
    });
  }

  /**
   * プレイエリアの器を描く。
   * 内側は半透明にして背景の情景をうっすら透かし、料理が浮いて見えないようにする。
   */
  /** 器の絵（スキンを替えたら描き直す）。 */
  private bowlShadow?: Phaser.GameObjects.Graphics;
  private drawnSkinId = '';
  private bowlGraphics?: Phaser.GameObjects.Graphics;

  private drawBowl(): void {
    const bowl = this.bowl;
    const { shell } = GAME_TUNING.world.bowl;
    const skin = bowlSkinById(this.context.settings.bowlSkin);
    this.drawnSkinId = this.context.settings.bowlSkin;
    const accent =
      skin.rim ?? Phaser.Display.Color.HexStringToColor(this.context.stage.theme.accentColor).color;

    this.bowlShadow?.destroy();
    this.bowlGraphics?.destroy();

    // 器の下に落ちる影（カウンターに置かれている感じ）
    const shadow = this.add.graphics().setDepth(-2);
    shadow.fillStyle(0x000000, 0.34);
    shadow.fillEllipse(bowl.centerX, bowl.bottomY + shell + 10, bowl.rimHalf * 2.05, 34);
    this.bowlShadow = shadow;

    const g = this.add.graphics().setDepth(-1);
    this.bowlGraphics = g;

    // 胴（外側の輪郭と内側の輪郭で囲んだ帯）
    const outer = offsetContour(bowl, shell);
    const band = [...outer, ...[...bowl.contour].reverse()];
    g.fillStyle(skin.body, 1);
    g.fillPoints(band, true);
    // 胴の左側に光を足して丸みを出す
    g.fillStyle(0xffffff, skin.highlight);
    g.fillPoints(
      [
        ...outer.slice(0, Math.floor(outer.length / 2)),
        ...bowl.contour.slice(0, Math.floor(bowl.contour.length / 2)).reverse(),
      ],
      true,
    );
    this.drawBowlPattern(g, skin);

    // 高台（足）
    g.fillStyle(skin.foot, 1);
    g.fillPoints(
      [
        { x: bowl.centerX - 54, y: bowl.bottomY + shell - 4 },
        { x: bowl.centerX + 54, y: bowl.bottomY + shell - 4 },
        { x: bowl.centerX + 44, y: bowl.bottomY + shell + 14 },
        { x: bowl.centerX - 44, y: bowl.bottomY + shell + 14 },
      ],
      true,
    );

    // 器の内側。背景の情景を透かすよう薄く重ねる
    g.fillStyle(skin.inner, skin.innerAlpha);
    g.fillPoints([...bowl.contour], true);
    // 内壁の照り（縁の近くを明るく）
    g.fillStyle(0xffe6c0, 0.035);
    g.fillEllipse(bowl.centerX, bowl.rimY + 16, bowl.rimHalf * 1.5, 46);
    // 底の溜まり
    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(bowl.centerX, bowl.bottomY - 22, bowl.rimHalf * 0.9, 54);

    // 縁の線（口の位置を分かりやすくする）
    g.lineStyle(3, accent, 0.9);
    g.strokePoints([...bowl.contour], false);
    g.lineStyle(1, 0xfff0c8, 0.4);
    g.strokePoints([...offsetContour(bowl, 2.5)], false);

    // 口をまたぐ渡し線（器の口の高さを視覚的に閉じる）
    g.lineStyle(2, accent, 0.28);
    g.beginPath();
    g.moveTo(bowl.lipLeft.x, bowl.lipLeft.y);
    g.lineTo(bowl.lipRight.x, bowl.lipRight.y);
    g.strokePath();
  }

  /** 胴の帯に模様を描く（唐草・線・金粉・流し釉・金継ぎ）。 */
  private drawBowlPattern(g: Phaser.GameObjects.Graphics, skin: BowlSkin): void {
    if (skin.pattern === 'none') return;
    const bowl = this.bowl;
    const { shell } = GAME_TUNING.world.bowl;
    const mid = offsetContour(bowl, shell / 2);
    const outer = offsetContour(bowl, shell);
    const color = skin.patternColor;
    // 決まった並びの乱数（毎回同じ模様になるように）
    let seed = 7;
    const rand = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    switch (skin.pattern) {
      case 'karakusa':
        // 唐草：帯の中央を波打つ線と、ところどころの渦
        g.lineStyle(1.6, color, 0.9);
        g.beginPath();
        mid.forEach((p, i) => {
          const q = outer[i];
          const nx = (q.x - p.x) / (shell / 2);
          const ny = (q.y - p.y) / (shell / 2);
          const w = Math.sin(i * 1.3) * 3;
          if (i === 0) g.moveTo(p.x + nx * w, p.y + ny * w);
          else g.lineTo(p.x + nx * w, p.y + ny * w);
        });
        g.strokePath();
        mid.forEach((p, i) => {
          if (i % 3 === 1) g.strokeCircle(p.x, p.y, 2.2);
        });
        break;
      case 'stripes':
        // 放射状の藍の線
        g.lineStyle(2, color, 0.85);
        bowl.contour.forEach((p, i) => {
          if (i % 2 !== 0) return;
          const q = outer[i];
          g.lineBetween(p.x, p.y, q.x, q.y);
        });
        break;
      case 'flecks':
        // 金粉・土の粒
        g.fillStyle(color, 0.9);
        for (let i = 0; i < 90; i += 1) {
          const k = Math.floor(rand() * (mid.length - 1));
          const t = rand();
          const p = bowl.contour[k];
          const q = outer[k];
          g.fillCircle(p.x + (q.x - p.x) * t, p.y + (q.y - p.y) * t, 0.6 + rand() * 1.2);
        }
        break;
      case 'drip':
        // 縁から垂れる釉薬
        g.fillStyle(color, 0.55);
        outer.forEach((q, i) => {
          if (i % 3 !== 0 || i === 0 || i === outer.length - 1) return;
          const p = bowl.contour[i];
          g.fillEllipse((p.x + q.x) / 2, (p.y + q.y) / 2, 5, 9 + rand() * 6);
        });
        break;
      case 'kintsugi': {
        // 金継ぎ：胴を横切る金の継ぎ目が2本
        g.lineStyle(2.2, color, 0.95);
        [0.25, 0.68].forEach((start) => {
          const k = Math.floor(start * (mid.length - 1));
          const p = bowl.contour[k];
          const q = outer[k];
          g.beginPath();
          g.moveTo(p.x, p.y);
          g.lineTo((p.x + q.x) / 2 + 3, (p.y + q.y) / 2 - 2);
          g.lineTo(q.x, q.y);
          g.strokePath();
          g.beginPath();
          const m = mid[Math.min(mid.length - 1, k + 1)];
          g.moveTo((p.x + q.x) / 2 + 3, (p.y + q.y) / 2 - 2);
          g.lineTo(m.x, m.y);
          g.strokePath();
        });
        break;
      }
    }
  }

  /**
   * 「そろそろこぼれる」警告。
   * ゲームオーバーの境界線ではなく、器の縁そのものを光らせて危険を伝える。
   */
  private drawGameOverLine(danger: number): void {
    const bowl = this.bowl;
    const graphics = this.lineGraphics;
    graphics.clear();
    this.spillLabel?.setAlpha(danger > 0 ? Math.min(1, 0.7 + danger * 0.3) : 0);
    if (danger <= 0) return;

    // 縁のリップに沿って赤く光らせる（ここを越えると落ちる、という表現）
    const contour = this.bowl.contour;
    graphics.lineStyle(5, 0xff6b5a, 0.28 + danger * 0.6);
    graphics.beginPath();
    contour.forEach((point, index) => {
      if (index === 0) graphics.moveTo(point.x, point.y);
      else graphics.lineTo(point.x, point.y);
    });
    graphics.strokePath();

    // 口の内側をうっすら赤く染めて、積み上がりすぎを知らせる
    const top = GAME_TUNING.world.spawnY - 40;
    graphics.fillStyle(0xff6b5a, 0.1 * danger);
    graphics.fillRect(
      bowl.centerX - bowl.rimHalf,
      top,
      bowl.rimHalf * 2,
      Math.max(0, bowl.rimY - top),
    );
  }

  /* --------------------------------- 入力 --------------------------------- */

  private setupInput(): void {
    this.input.mouse?.disableContextMenu();

    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (!this.context.state.acceptsInput || this.pickMode) return;
      this.setDropX(pointer.worldX);
    });

    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (!this.context.state.acceptsInput || this.pickMode) return;
      this.setDropX(pointer.worldX);
    });

    this.input.on(Phaser.Input.Events.POINTER_UP, (pointer: Phaser.Input.Pointer) => {
      if (!this.context.state.acceptsInput) return;
      if (this.pickMode) {
        this.tryPickAt(pointer.worldX, pointer.worldY);
        return;
      }
      // 盤面のタップは必ず料理を落とす。豆知識は盤面の外の「✨豆知識」ボタンから開く。
      // （マーカーが落とす位置と同じ高さにあり、落とすつもりのタップでカードが開いて止まっていた）
      this.setDropX(pointer.worldX);
      this.tryDrop();
    });
  }

  private bindCommands(): void {
    const bus = this.context.bus;

    this.unsubscribers.push(
      bus.on('command:start', () => this.startGame()),
      bus.on('command:restart', () => this.restart()),
      bus.on('command:pause', () => this.pauseGame()),
      bus.on('command:resume', () => this.resumeGame()),
      bus.on('command:togglePause', () => {
        if (this.context.state.is(GameState.PLAYING)) this.pauseGame();
        else if (this.context.state.is(GameState.PAUSED)) this.resumeGame();
      }),
      bus.on('command:move', ({ direction }) => {
        if (!this.context.state.acceptsInput) return;
        this.setDropX(this.dropX + direction * GAME_TUNING.drop.keyboardStep);
      }),
      bus.on('command:drop', () => {
        if (!this.context.state.acceptsInput || this.pickMode) return;
        this.tryDrop();
      }),
      bus.on('command:continue', () => this.revive()),
      bus.on('command:setMode', ({ relaxed }) => {
        this.relaxed = relaxed;
        bus.emit('mode:change', { relaxed });
      }),
      bus.on('command:finish', () => {
        if (!this.relaxed) return;
        if (this.context.state.is(GameState.PLAYING, GameState.PAUSED)) this.finishGame();
      }),
      bus.on('command:pickStart', () => this.setPickMode(true)),
      bus.on('command:pickCancel', () => this.setPickMode(false)),
      bus.on('command:pickConfirm', () => this.confirmPick()),
      bus.on('settings:change', () => {
        this.guideGraphics.setVisible(this.context.settings.showGuide);
        if (this.drawnSkinId !== this.context.settings.bowlSkin) {
          this.drawnSkinId = this.context.settings.bowlSkin;
          this.drawBowl();
        }
      }),
      // 追悼・慰霊・聖域の景色が映っているあいだは画面を揺らさない。
      bus.on('scene:solemn', ({ solemn }) => {
        this.effects.setSolemn(solemn);
      }),
    );
  }

  private setDropX(x: number): void {
    const item = this.currentItem();
    const { min, max } = this.bowl.dropRange(this.shapeFor(item).halfWidth);
    this.dropX = Phaser.Math.Clamp(x, min, max);
  }

  /* ------------------------------- ゲーム進行 ------------------------------- */

  private startGame(): void {
    if (this.context.state.is(GameState.PLAYING)) return;
    if (this.context.state.is(GameState.GAME_OVER)) {
      this.restart();
      return;
    }
    if (!this.context.state.transition(GameState.PLAYING)) return;
    this.context.assist.resetRun();
    this.scoreCommittedToTotal = 0;
    this.dropsThisRun = 0;
    this.runStartedAt = this.gameTime;
    this.resumeWorld();
    this.context.storage.incrementPlayCount();
    this.context.audio.unlock();
    this.context.audio.playBgm();
  }

  private pauseGame(): void {
    if (this.ending) return;
    this.setPickMode(false);
    if (!this.context.state.transition(GameState.PAUSED)) return;
    this.pendingDrop.clear();
    this.persistTourElapsed();
    this.matter.world.pause();
    this.context.audio.stopBgm();
  }

  private resumeGame(): void {
    if (!this.context.state.transition(GameState.PLAYING)) return;
    this.resumeWorld();
    this.context.audio.playBgm();
  }

  private restart(): void {
    this.clearEnding();
    this.setPickMode(false);
    this.context.assist.resetRun();
    this.scoreCommittedToTotal = 0;
    this.persistTourElapsed();
    this.clearPieces();
    this.merge.clear();
    this.scoring.reset();
    this.gameOver.reset();
    this.spawn.reset();
    this.highestLevelCreated = 0;
    this.dropsThisRun = 0;
    this.runStartedAt = this.gameTime;
    this.context.bus.emit('run:maxLevel', { level: 0 });
    this.emitScenePhase();
    this.lastDropAt = Number.NEGATIVE_INFINITY;
    this.pendingDrop.clear();
    this.dangerRatio = -1;
    this.dangerActive = false;
    this.drawGameOverLine(0);

    const bg = this.scene.get(BACKGROUND_SCENE_KEY) as
      | import('./BackgroundScene').BackgroundScene
      | undefined;
    bg?.resetTour();

    this.context.bus.emit('score:change', { score: 0, gained: 0, combo: 0 });
    this.context.bus.emit('combo:change', { combo: 0, multiplier: 1 });
    this.emitQueueChange();

    if (!this.context.state.is(GameState.PLAYING)) {
      this.context.state.transition(GameState.READY);
      this.context.state.transition(GameState.PLAYING);
      this.context.storage.incrementPlayCount();
    }
    this.resumeWorld();
    this.context.audio.unlock();
    this.context.audio.playBgm();
  }

  private tryDrop(): void {
    if (!this.context.state.acceptsInput || this.ending) return;
    if (this.gameTime - this.lastDropAt < GAME_TUNING.drop.cooldownMs) {
      // 待ちのあいだのタップは捨てずに取っておく（最後の1回分だけ）
      this.pendingDrop.queue(this.dropX);
      return;
    }
    this.pendingDrop.clear();

    const level = this.spawn.advance();
    const piece = this.createPiece(level, this.dropX, GAME_TUNING.world.spawnY);
    if (piece) {
      piece.setVelocityY(GAME_TUNING.drop.initialVelocityY);
      this.chopsticksOpenMs = 160;
      this.lastDropAt = this.gameTime;
      this.dropsThisRun += 1;
      this.context.audio.playSe('drop', { level: piece.level });
    }
    this.emitQueueChange();
    this.updatePreview();
  }

  /** 取っておいたタップを、待ちが明けたら落とす。落とせない状態なら捨てる。 */
  private flushPendingDrop(): void {
    if (!this.pendingDrop.pending) return;
    if (!this.context.state.acceptsInput || this.ending || this.pickMode) {
      this.pendingDrop.clear();
      return;
    }
    const x = this.pendingDrop.take(this.gameTime, this.lastDropAt, GAME_TUNING.drop.cooldownMs);
    if (x === null) return;
    this.setDropX(x);
    this.tryDrop();
  }

  private updatePreview(): void {
    const item = this.currentItem();
    const { spawnY } = GAME_TUNING.world;
    const bottomY = this.bowl.bottomY;

    this.previewImage.setTexture(this.textureKeyFor(item));
    this.applyShapeToImage(this.previewImage, this.shapeFor(item));
    this.previewImage.setPosition(this.dropX, spawnY);
    const showPreview = this.context.state.is(GameState.PLAYING, GameState.READY);
    this.previewImage.setVisible(showPreview);
    this.chopsticksOpenMs = Math.max(0, this.chopsticksOpenMs - this.game.loop.delta);
    this.drawChopsticks(this.dropX, spawnY - this.shapeFor(item).halfWidth, showPreview && !this.pickMode);

    this.guideGraphics.clear();
    if (!this.context.settings.showGuide || !this.context.state.is(GameState.PLAYING)) return;

    this.guideGraphics.lineStyle(2, 0xffe6b8, GAME_TUNING.drop.guideAlpha);
    for (let y = spawnY + item.radius + 6; y < bottomY; y += 14) {
      this.guideGraphics.beginPath();
      this.guideGraphics.moveTo(this.dropX, y);
      this.guideGraphics.lineTo(this.dropX, Math.min(y + 7, bottomY));
      this.guideGraphics.strokePath();
    }
  }

  /* --------------------------------- 合体 --------------------------------- */

  private setupCollisions(): void {
    this.matter.world.on(
      Phaser.Physics.Matter.Events.COLLISION_START,
      (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => {
        if (!this.context.state.simulates) return;
        event.pairs.forEach((pair) => {
          const a = this.bodyIndex.get(pair.bodyA.id);
          const b = this.bodyIndex.get(pair.bodyB.id);
          // 見た目の弾力（当たった強さだけスプライトを潰す。物理は変えない）
          this.applyImpact(pair.bodyA, pair.bodyB, a, b);
          if (!a || !b) return;
          this.merge.request(a, b);
        });
      },
    );
  }

  /** 衝突の相対速度から「潰れ」の強さを求めて反映する。 */
  private applyImpact(
    bodyA: MatterJS.BodyType,
    bodyB: MatterJS.BodyType,
    a: FoodPiece | undefined,
    b: FoodPiece | undefined,
  ): void {
    if (!a && !b) return;
    const relative = Math.hypot(
      bodyA.velocity.x - bodyB.velocity.x,
      bodyA.velocity.y - bodyB.velocity.y,
    );
    const jiggle = GAME_TUNING.jiggle;
    if (relative <= jiggle.impactSpeedThreshold) return;
    const strength = (relative - jiggle.impactSpeedThreshold) / jiggle.impactSpeedRange;
    a?.impact(strength);
    b?.impact(strength);

    // 当たった場所に小さな波紋（合体する組み合わせは合体演出に任せる）
    if (a && b && a.level === b.level) return;
    const piece = a ?? b!;
    const other = piece === a ? bodyB : bodyA;
    const px = (piece.x + other.position.x) / 2;
    const py = (piece.y + other.position.y) / 2;
    const color = Phaser.Display.Color.HexStringToColor(piece.item.icon.baseColor).color;
    if (this.effects.playImpact(px, py, strength, color)) {
      this.context.audio.playSe('impact', { level: piece.level, strength: Math.min(1, strength) });
    }
  }

  /**
   * 物理更新後に安全に合体を処理する。
   * 衝突イベント内では削除せず、必ずここで実行する。
   */
  private processMerges(): void {
    const requests = this.merge.drain(GAME_TUNING.merge.maxPerFrame);
    requests.forEach((request) => this.resolveMerge(request));
  }

  private resolveMerge(request: MergeRequest): void {
    if (!request.a.alive || !request.b.alive) {
      this.merge.cancel(request);
      return;
    }

    if (request.type === 'max-level-bonus') {
      const total = this.scoring.addBonus(GAME_TUNING.merge.maxLevelBonus);
      this.effects.playBonus(request.x, request.y, GAME_TUNING.merge.maxLevelBonus);
      this.context.audio.playSe('bonus');
      this.context.bus.emit('score:change', {
        score: total,
        gained: GAME_TUNING.merge.maxLevelBonus,
        combo: this.scoring.combo,
      });
      return;
    }

    const x = (request.a.x + request.b.x) / 2;
    const y = (request.a.y + request.b.y) / 2;

    this.removePiece(request.a.id);
    this.removePiece(request.b.id);

    const created = this.createPiece(request.nextLevel, x, y, { pop: true });
    if (!created) return;

    const result = this.scoring.registerMerge({
      level: created.level,
      itemScore: created.item.score,
      now: this.gameTime,
    });

    this.effects.playMerge({
      x: created.x,
      y: created.y,
      item: created.item,
      gained: result.gained,
      combo: result.combo,
      radius: created.radius,
    });
    if (created.level >= this.context.maxLevel) {
      this.context.audio.playSe('top');
    } else {
      this.context.audio.playSe(result.combo >= 2 ? 'combo' : 'merge', {
        level: created.level,
        combo: result.combo,
      });
    }

    this.context.bus.emit('score:change', {
      score: result.total,
      gained: result.gained,
      combo: result.combo,
    });
    this.context.bus.emit('combo:change', {
      combo: result.combo,
      multiplier: result.multiplier,
    });

    if (created.level >= GAME_TUNING.merge.highlightMinLevel) {
      this.context.bus.emit('merge:big', { level: created.level });
    }

    const isNew = this.context.storage.unlockItem(created.item.id);
    if (isNew) {
      this.context.audio.playSe('unlock');
      this.context.bus.emit('dex:unlock', { item: created.item });
    }
    // 何ができたかを名前で見せる（はじめて作った料理・大きな料理）
    const isTop = created.level >= this.context.maxLevel;
    if (isTop) {
      // 最上位の一品：カットイン
      this.effects.topCutIn(created.item, this.textureKeyFor(created.item), this.context.stage.regionName);
    } else if ((isNew && created.level >= 2) || created.level >= GAME_TUNING.effects.bannerMinLevel) {
      this.effects.banner(created.item, isNew, false);
    }
  }

  /** collisionstart を取り逃した場合の保険（スリープ中の重なりなど）。 */
  private runSafetyScan(): void {
    if (this.gameTime - this.lastSafetyScanAt < GAME_TUNING.merge.safetyScanIntervalMs) return;
    this.lastSafetyScanAt = this.gameTime;

    const list = [...this.pieces.values()].filter((piece) => piece.alive && !piece.isMerging);
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i];
        const b = list[j];
        if (a.isMerging || b.isMerging || a.level !== b.level) continue;
        const distance = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
        const touch = (a.radius + b.radius) * GAME_TUNING.merge.safetyScanTouchRatio;
        if (distance <= touch) this.merge.request(a, b);
      }
    }
  }

  /* ------------------------------ ゲームオーバー ------------------------------ */

  private updateGameOver(delta: number): void {
    // ゲームオーバーは「器の外へこぼれて落ちた」ときだけ。
    // 器の外側には床が無いので、縁を越えた料理はそのまま落ちていく。
    const spilled = findSpilledPiece(this.pieces.values(), {
      rimY: this.bowl.rimY,
      halfWidthAt: (y) => this.bowl.halfWidthAt(y),
      centerX: this.bowl.centerX,
      marginPx: GAME_TUNING.world.spillMarginPx,
      fallOutY: GAME_TUNING.world.fallOutY,
    });
    if (spilled && this.relaxed) {
      // のんびりモードでは、こぼれた料理がそっと消えるだけ
      const piece = this.pieces.get(spilled.id);
      if (piece) this.effects.burst(piece.x, Math.min(piece.y, this.bowl.rimY), 0xffffff, 0.6);
      this.removePiece(spilled.id);
      return;
    }
    if (spilled) {
      const piece = this.pieces.get(spilled.id);
      if (piece) this.beginEnding(piece);
      else this.finishGame();
      return;
    }

    // 口より上まで積み上がっている間は「そろそろこぼれる」警告を出すだけ（終了はしない）。
    // 口より上でも真ん中に載っているだけなら安全なので数えない。縁に寄っているものだけ。
    // （中盤以降ずっと赤く光っていて、警告として効いていなかった）
    const edge = GAME_TUNING.world.bowl.rimHalf * GAME_TUNING.gameOver.edgeRatio;
    const nearEdge = this.relaxed
      ? []
      : [...this.pieces.values()].filter(
          (piece) => Math.abs(piece.x - this.bowl.centerX) + piece.radius >= edge,
        );
    const overflowing = findViolatingPiece(nearEdge, this.bowl.rimY, this.gameTime, {
      thresholdMs: GAME_TUNING.gameOver.warningMs,
      ignoreNewPieceMs: GAME_TUNING.gameOver.ignoreNewPieceMs,
      lineMarginPx: GAME_TUNING.gameOver.lineMarginPx,
    });

    this.gameOver.update(delta, overflowing !== null);
    const danger = Math.round(this.gameOver.progress * 10) / 10;
    if (danger !== this.dangerRatio) {
      this.dangerRatio = danger;
      this.drawGameOverLine(danger);
    }
    // 「こぼれそう！」が0.6秒続いたら知らせる（ちらつきで何度も出入りしないよう、満ちたときだけ）
    const dangerActive = danger >= 1;
    if (dangerActive !== this.dangerActive) {
      this.dangerActive = dangerActive;
      this.context.bus.emit('board:danger', { active: dangerActive });
    }
  }

  private finishGame(): void {
    this.setPickMode(false);
    if (!this.context.state.transition(GameState.GAME_OVER)) return;
    this.persistTourElapsed();
    this.matter.world.pause();
    this.context.audio.stopBgm();
    this.context.audio.playSe('gameOver');

    const score = this.scoring.score;
    const relaxed = this.relaxed;
    // のんびりモードの記録はハイスコア・メダルに入れない（図鑑は合体のたびに埋まっている）
    const assisted = this.context.assist.assisted;
    const isNewRecord = relaxed ? false : this.context.storage.submitScore(score, { assisted });
    if (!relaxed) this.context.storage.submitMaxLevel(this.highestLevelCreated, { assisted });

    // 累計スコアを加算。これで「何度もプレイすればいずれ解放される」
    // ごほうび導線が稼働する。
    // コンティニューしたプレイは2回ゲームオーバーになるので、前回までに足した分は除く。
    const gain = relaxed ? 0 : Math.max(0, score - this.scoreCommittedToTotal);
    this.scoreCommittedToTotal = score;
    const total = this.context.storage.addTotalScore(gain);

    // 解放状況を更新し、新たに「食べに行ける」アイテムがあれば通知する。
    const before = computeAllRewardStatuses(
      this.context.stage,
      this.context.storage.seenItemIds,
      total - gain,
    );
    const after = computeAllRewardStatuses(
      this.context.stage,
      this.context.storage.seenItemIds,
      total,
    );
    const newlyAvailable = findNewlyAvailableRewards(this.context.stage, before, after);
    this.context.bus.emit('total:change', { totalScore: total });
    newlyAvailable.forEach((item) => this.context.bus.emit('reward:unlock', { item }));

    if (isNewRecord) {
      this.context.bus.emit('highscore:change', { highScore: this.context.storage.highScore });
    }

    this.context.bus.emit('game:over', {
      score,
      highScore: this.context.storage.highScore,
      isNewRecord,
      maxLevelReached: this.highestLevelCreated,
      assisted,
      relaxed,
      drops: this.dropsThisRun,
      playMs: Math.max(0, Math.round(this.gameTime - this.runStartedAt)),
      spilled: relaxed ? undefined : this.spilledInfo,
      maxCombo: this.scoring.maxCombo,
    });
    this.spilledInfo = undefined;
  }

  /**
   * こぼれた料理を1秒ほど赤く見せてから結果画面を出す。
   * すぐに結果画面で隠すと「何で終わったのか」が見えず、納得感がなかった。
   */
  private beginEnding(piece: FoodPiece): void {
    if (this.ending) return;
    this.ending = true;
    this.spilledInfo = {
      name: piece.item.shortName ?? piece.item.name,
      level: piece.item.level,
      side: piece.x < this.bowl.centerX ? 'left' : 'right',
    };
    this.setPickMode(false);
    this.matter.world.pause();
    piece.sprite.setTint(0xff6b5a);
    const ring = this.add.graphics().setDepth(50);
    ring.lineStyle(5, 0xff4d3d, 0.95);
    ring.strokeCircle(piece.x, piece.y, piece.radius + 8);
    this.endingMarker = ring;
    this.context.bus.emit('run:ending');
    this.tweens.add({
      targets: ring,
      alpha: { from: 1, to: 0.25 },
      duration: 260,
      yoyo: true,
      repeat: 2,
    });
    this.effects.shake(0.6);
    this.time.delayedCall(GAME_TUNING.gameOver.revealSpillMs, () => {
      if (!this.ending) return;
      this.finishGame();
    });
  }

  private clearEnding(): void {
    this.ending = false;
    this.endingMarker?.destroy();
    this.endingMarker = undefined;
    this.pieces.forEach((piece) => piece.sprite.clearTint());
  }

  /* ------------------------------ コンティニュー ------------------------------ */

  /**
   * ゲームオーバーから再開する（広告を見終わったあとに UI から呼ばれる）。
   * こぼれた料理をすべてと、上から数個を消して、同じ盤面の続きから始める。
   * スコア・到達レベル・コンボはそのまま引き継ぐ。
   */
  private revive(): void {
    if (!this.context.state.is(GameState.GAME_OVER)) return;
    this.clearEnding();

    const bounds = {
      rimY: this.bowl.rimY,
      halfWidthAt: (y: number) => this.bowl.halfWidthAt(y),
      centerX: this.bowl.centerX,
      marginPx: GAME_TUNING.world.spillMarginPx,
      fallOutY: GAME_TUNING.world.fallOutY,
    };
    for (let guard = 0; guard < 64; guard += 1) {
      const spilled = findSpilledPiece(this.pieces.values(), bounds);
      if (!spilled) break;
      this.removePiece(spilled.id);
    }

    const top = [...this.pieces.values()]
      .filter((piece) => piece.alive)
      .sort((a, b) => a.y - a.radius - (b.y - b.radius))
      .slice(0, ASSIST_TUNING.continueClearTop);
    top.forEach((piece) => {
      this.effects.burst(piece.x, piece.y, 0xffffff, 0.8);
      this.removePiece(piece.id);
    });

    this.gameOver.reset();
    this.dangerRatio = -1;
    this.dangerActive = false;
    this.drawGameOverLine(0);
    this.lastDropAt = this.gameTime;
    this.pendingDrop.clear();

    if (!this.context.state.transition(GameState.PLAYING)) return;
    this.resumeWorld();
    this.context.audio.playBgm();
    this.context.bus.emit('run:continued');
  }

  /**
   * 物理を再開する。
   *
   * Phaser の `world.resume()` は runner.timeLastTick に performance.now() を入れるが、
   * update に渡ってくる time はゲームループの時計で、端末によっては少し遅れている。
   * 差が負になると frameDelta が負のまま記録され、料理が空中で止まったままになる
   * （ヘッドレスの Chromium で再現。低スペック端末でも起きうる）。
   * timeLastTick を 0 にしておけば、次の1フレームは直前の正しい frameDelta で進む。
   */
  private resumeWorld(): void {
    const world = this.matter.world;
    world.resume();
    const runner = world.runner as unknown as {
      timeLastTick: number;
      frameDelta: number;
      frameDeltaHistory?: number[];
    };
    runner.timeLastTick = 0;
    if (!(runner.frameDelta > 0)) runner.frameDelta = 1000 / 60;
    if (runner.frameDeltaHistory) {
      runner.frameDeltaHistory = runner.frameDeltaHistory.filter((d) => d > 0);
    }
  }

  /* ------------------------------ おはし ------------------------------ */

  private setPickMode(active: boolean): void {
    if (active === this.pickMode) return;
    if (active) {
      if (!this.context.state.is(GameState.PLAYING)) return;
      if (!this.context.assist.canUseItemThisRun() || this.context.assist.items <= 0) return;
      if (this.pieces.size === 0) return;
      this.pickMode = true;
      this.pick.start();
      this.pendingDrop.clear();
      this.matter.world.pause();
    } else {
      this.pickMode = false;
      this.pick.stop();
      this.drawPickRing(null);
      this.context.bus.emit('pick:select', { name: null });
      if (this.context.state.is(GameState.PLAYING)) this.resumeWorld();
    }
    this.context.bus.emit('pick:mode', { active: this.pickMode });
  }

  /**
   * 選ぶモード中のタップ。料理に当たったら、それを光らせて「この料理を消す？」を出すだけ（まだ減らさない）。
   * 外れたら選んだのを取り消す。毎回確かめる（ボタンの押し間違いで有料のおはしが減っていた。2026-10-05）。
   */
  private tryPickAt(worldX: number, worldY: number): void {
    const target = this.findPieceAt(worldX, worldY);
    if (!target) {
      this.pick.unselect();
      this.drawPickRing(null);
      this.context.bus.emit('pick:select', { name: null });
      return;
    }
    this.pick.select(target.id);
    this.drawPickRing(target);
    this.context.bus.emit('pick:select', { name: target.item.name, level: target.level });
  }

  /** 「はい」：選んだ料理を消す。ここで初めておはしを1本使う。 */
  private confirmPick(): void {
    const selected = this.pick.selected;
    const target = selected === null ? undefined : this.pieces.get(selected);
    if (!this.pickMode || !target || !target.alive) {
      this.setPickMode(false);
      return;
    }
    const id = this.pick.confirm();
    if (id !== null) {
      this.effects.burst(target.x, target.y, 0xfff3c4, 1);
      this.removePiece(id);
    }
    this.setPickMode(false);
  }

  /** 選んだ料理のまわりに光る輪を描く（null で消す）。物理は止めてあるので、描きっぱなしでずれない。 */
  private drawPickRing(target: FoodPiece | null): void {
    if (!this.pickRing || !this.pickRing.scene) this.pickRing = this.add.graphics().setDepth(20);
    const g = this.pickRing;
    g.clear();
    if (!target) return;
    const r = target.radius + 6;
    g.fillStyle(0xfff3c4, 0.28);
    g.fillCircle(target.x, target.y, r);
    g.lineStyle(16, 0xffd166, 0.4);
    g.strokeCircle(target.x, target.y, r + 5);
    g.lineStyle(6, 0xfff2b0, 1);
    g.strokeCircle(target.x, target.y, r);
  }

  /** 指の位置にある料理（重なっていれば中心がいちばん近いもの）。指の太さぶん少し甘くする。 */
  private findPieceAt(x: number, y: number): FoodPiece | null {
    let best: FoodPiece | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const piece of this.pieces.values()) {
      if (!piece.alive || piece.isMerging) continue;
      const distance = Phaser.Math.Distance.Between(x, y, piece.x, piece.y);
      if (distance <= piece.radius + 8 && distance < bestDistance) {
        best = piece;
        bestDistance = distance;
      }
    }
    return best;
  }

  /* ------------------------------ ピース管理 ------------------------------ */

  /** 盤面にある料理の数（デバッグ / E2E テスト用）。 */
  get pieceCount(): number {
    return this.pieces.size;
  }

  private createPiece(
    level: number,
    x: number,
    y: number,
    options: { pop?: boolean } = {},
  ): FoodPiece | null {
    const item = this.context.itemByLevel.get(level);
    if (!item) {
      console.warn(`[GameScene] レベル ${level} のアイテムが見つかりません`);
      return null;
    }

    // 器の内側に収まる位置へ丸める（壁へめり込ませない）
    const shape = this.shapeFor(item);
    const clampedY = Math.min(y, this.bowl.deepestY(shape.radius));
    const room = Math.max(0, this.bowl.halfWidthAt(clampedY) - shape.halfWidth - 1);
    const clampedX = Phaser.Math.Clamp(x, this.bowl.centerX - room, this.bowl.centerX + room);

    const piece = new FoodPiece({
      scene: this,
      item,
      textureKey: this.textureKeyFor(item),
      shape,
      id: this.nextPieceId,
      x: clampedX,
      y: clampedY,
      createdAt: this.gameTime,
      steamTextureKey:
        item.level >= GAME_TUNING.scenery.steamMinLevel ? STEAM_TEXTURE_KEY : undefined,
    });
    this.nextPieceId += 1;

    this.pieces.set(piece.id, piece);
    this.bodyIndex.set(piece.bodyId, piece);
    if (piece.level > this.highestLevelCreated) {
      this.highestLevelCreated = piece.level;
      this.emitScenePhase();
      this.context.bus.emit('run:maxLevel', { level: piece.level });
    }

    if (options.pop) piece.playPop();
    else piece.sync(0);

    return piece;
  }

  /**
   * 到達レベルに応じて情景の時間帯を通知する。
   * 進むほど夕暮れ→薄暮→夜→深夜→夜明けになり、街の灯りや演出が増えていく。
   */
  private emitScenePhase(): void {
    const { evening, night, deepnight, dawn } = GAME_TUNING.scenery.phase;
    const level = this.highestLevelCreated;
    // BGM も到達レベルに合わせて層を増やし、夜が深まるほど賑やかにする
    this.context.audio.setBgmIntensity(
      Phaser.Math.Clamp((level - 2) / (this.context.maxLevel - 2), 0, 1),
    );
    const steps: Array<[number, ScenePhase]> = [
      [dawn, 'dawn'],
      [deepnight, 'deepnight'],
      [night, 'night'],
      [evening, 'evening'],
    ];
    const phase: ScenePhase = steps.find(([min]) => level >= min)?.[1] ?? 'dusk';
    if (phase === this.scenePhase) return;
    this.scenePhase = phase;
    this.context.bus.emit('scene:phase', { phase, level });
  }

  private removePiece(pieceId: number): void {
    const piece = this.pieces.get(pieceId);
    if (!piece) return;
    this.pieces.delete(pieceId);
    this.bodyIndex.delete(piece.bodyId);
    this.merge.forgetPiece(pieceId);
    piece.destroy();
    this.wakeAllPieces();
  }

  /**
   * 残っている料理をすべて起こす。
   *
   * Matter は「衝突」でしかスリープを解除しないため、合体で下の料理が消えても
   * その上に載っていた料理は眠ったまま動かず、宙に浮いて見えてしまう。
   * （プレイヤーからは「くっついていないのに止まっている」バグに見える。）
   * 料理が減った直後に全体を起こしておけば、支えを失った山は必ず崩れ落ちる。
   */
  private wakeAllPieces(): void {
    for (const piece of this.pieces.values()) {
      if (!piece.alive) continue;
      piece.wake();
    }
  }

  private clearPieces(): void {
    [...this.pieces.keys()].forEach((id) => this.removePiece(id));
    this.pieces.clear();
    this.bodyIndex.clear();
  }

  private currentItem(): StageItemDefinition {
    return this.context.itemByLevel.get(this.spawn.current) ?? this.context.items[0];
  }

  private nextItem(): StageItemDefinition {
    return this.context.itemByLevel.get(this.spawn.next) ?? this.context.items[0];
  }

  private emitQueueChange(): void {
    this.context.bus.emit('queue:change', {
      current: this.currentItem(),
      next: this.nextItem(),
    });
  }

  /**
   * 実画像が読み込めていれば輪郭付きの形、そうでなければ円。
   * 横長で1つ上の段より幅が広い料理は、shapeScales の倍率で当たり判定と絵を一緒に縮める。
   */
  private shapeFor(item: StageItemDefinition): ResolvedShape {
    return resolveItemShape(
      item,
      this.textures.exists(imageTextureKey(item)),
      shapeScaleOf(stageIdOf(this.context.stage), item.id),
    );
  }

  private applyShapeToImage(image: Phaser.GameObjects.Image, shape: ResolvedShape): void {
    image.setOrigin(shape.originX, shape.originY);
    image.setDisplaySize(shape.displaySize, shape.displaySize);
  }

  private textureKeyFor(item: StageItemDefinition): string {
    const imageKey = imageTextureKey(item);
    if (this.textures.exists(imageKey)) return imageKey;
    return placeholderTextureKey(item);
  }

  private teardown(): void {
    this.unsubscribers.forEach((dispose) => dispose());
    this.unsubscribers.length = 0;
    this.clearPieces();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
